import type { TargetsMap } from '@nono/contract';
import type { AccentKey } from './chart';

/*
 * Presentation grouping for the metrics table: each metric key → a funnel
 * family. Drives the two-row family-banded header and the accent on group
 * headers (color = family identity, used sparingly). This is a UI concern, so
 * it lives in the web app, not the frozen semantic layer.
 */
export type Family = 'spend' | 'click' | 'lead' | 'booking' | 'showup' | 'close' | 'money';

export const FAMILY_ACCENT: Record<Family, AccentKey> = {
  spend: 'spend',
  click: 'click',
  lead: 'lead',
  booking: 'booking',
  showup: 'showup',
  close: 'close',
  money: 'money',
};

const FAMILY_OF: Record<string, Family> = {
  // spend
  adSpend: 'spend',
  adSpendAed: 'spend',
  cpm: 'spend',
  // click / traffic
  impressions: 'click',
  reach: 'click',
  clicks: 'click',
  ctr: 'click',
  cpc: 'click',
  // lead funnel (lead segment)
  leads: 'lead',
  // booking funnel
  bookings: 'booking',
  lpPct: 'booking',
  cpl: 'booking',
  costPerBooking: 'booking',
  // show-up
  showedUp: 'showup',
  surPct: 'showup',
  surCost: 'showup',
  // close
  soldMemberships: 'close',
  ccPct: 'close',
  lcPct: 'close',
  // money / P&L
  revenue: 'money',
  cash: 'money',
  cpa: 'money',
  rPl: 'money',
  cPl: 'money',
  rRoi: 'money',
  cRoi: 'money',
};

export function familyOf(key: string): Family {
  return FAMILY_OF[key] ?? 'money';
}

export type MetricStatus = 'good' | 'warn' | 'bad';

/**
 * Status of a value against its target. 'good' = meets target; 'warn' = within
 * 10% of it; 'bad' otherwise. Returns null when there's no target or no value
 * (no target = no verdict = neutral ink — color is reserved for decisions).
 */
export function evaluateStatus(
  value: number | null | undefined,
  key: string,
  targets: TargetsMap,
): MetricStatus | null {
  const target = targets[key];
  if (!target) return null;
  if (!target.active) return null;
  if (value === null || value === undefined || Number.isNaN(value)) return null;

  const { comparator, targetValue } = target;
  if (comparator === 'gte') {
    if (value >= targetValue) return 'good';
    if (value >= targetValue * 0.9) return 'warn';
    return 'bad';
  }
  // 'lte' — lower is better (costs)
  if (value <= targetValue) return 'good';
  if (value <= targetValue * 1.1) return 'warn';
  return 'bad';
}
